import { useTranslation } from 'react-i18next';
import type { Account, Id } from '@hero/schema';
import { useAccountListApi } from '@hero/api-hooks';
import { SharedList } from '../../../app/List/List';
import { AccountCard } from '../Card/AccountCard';
import { AccountDrawer } from '../Drawer/AccountDrawer';

export interface AccountsListPageProps {
  /**
   * Viewer org id — drives the per-card directional balance label
   * + sign. When omitted, cards fall back to a raw two-party
   * label (used in admin contexts).
   */
  viewerOrgId?: Id | string;
  /**
   * `true` (default) mounts the `<AccountDrawer />` here so this
   * page works as a standalone route. Set to `false` when the
   * host page already mounts one (e.g. embedded inside a CRM
   * surface).
   */
  renderDrawer?: boolean;
  /** Optional custom header rendered above the list. */
  header?: React.ReactNode;
  className?: string;
}

/**
 * Self-contained list of the viewer's bilateral accounts. Powered
 * by `useAccountListApi`. Each card opens the `AccountDrawer`,
 * which itself mounts the full per-account experience (invoices +
 * transactions tabs).
 *
 * Designed to be the replacement surface for the parked
 * `/wallet` route — same "everything financial in one place"
 * shape, but counterparty-centric instead of single-party.
 */
export function AccountsListPage({
  viewerOrgId,
  renderDrawer = true,
  header,
  className,
}: AccountsListPageProps) {
  const { t } = useTranslation(['m-account']);
  const query = useAccountListApi({});

  return (
    <div className={`flex flex-col gap-4 ${className ?? ''}`}>
      {header ?? (
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-gray-900">
            {t('page.list.title', { defaultValue: 'Accounts' })}
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            {t('page.list.description', {
              defaultValue:
                'One row per counterparty you trade with. The balance nets every direction.',
            })}
          </p>
        </div>
      )}

      <SharedList<Account>
        query={query}
        emptyMessage={t('page.list.empty', {
          defaultValue: 'No bilateral activity yet.',
        })}
        className="gap-3"
        renderItem={account => (
          <AccountCard account={account} viewerOrgId={viewerOrgId} />
        )}
      />

      {renderDrawer && <AccountDrawer asRole="auto" viewerOrgId={viewerOrgId} />}
    </div>
  );
}
